// PortfolioPublicView — the full public portfolio page body: the shared
// renderer on its `public` surface plus the "Powered by Aspire" footer. Used
// by the public /p page and the creator's owner-only draft preview, so the
// preview shows exactly what visitors will see (EPD-10093).

import type { PortfolioTheme } from "@/lib/portfolio/theme";
import { readableTextColor } from "@/lib/portfolio/theme-contrast";
import type { SocialStatsEntry } from "@/lib/queries/channel";
import type {
  PublicHighlightPostStats,
  PublicProfileData,
} from "@/lib/queries/public-profile";
import { PortfolioRenderer } from "./portfolio-renderer";

interface PortfolioPublicViewProps {
  data: PublicProfileData;
  theme: PortfolioTheme;
  socialStats?: SocialStatsEntry[];
  /** Stats still loading: hold the Social Accounts block's place (EPD-12469). */
  socialStatsPending?: boolean;
  highlightPostStats?: PublicHighlightPostStats;
  /** Fetches the contact email on a Contact click (live page, EPD-12502). */
  loadContactEmail?: () => Promise<string>;
}

export function PortfolioPublicView({
  data,
  theme,
  socialStats,
  socialStatsPending,
  highlightPostStats,
  loadContactEmail,
}: PortfolioPublicViewProps) {
  return (
    <div className="min-h-svh" style={{ background: theme.bg }}>
      {/* The page's one main landmark (EPD-12471); the footer stays outside
          it as the contentinfo landmark. No app layout wraps this route. */}
      <main>
        <PortfolioRenderer
          data={data}
          theme={theme}
          surface="public"
          socialStats={socialStats}
          socialStatsPending={socialStatsPending}
          highlightPostStats={highlightPostStats}
          loadContactEmail={loadContactEmail}
        />
      </main>
      <footer
        className="border-t px-6 py-8 text-center"
        style={{ borderColor: theme.border }}
      >
        <a
          href="https://aspire.io/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs transition-opacity hover:opacity-80"
          style={{ color: readableTextColor(theme.mutedText, [theme.bg]) }}
        >
          Powered by Aspire
        </a>
      </footer>
    </div>
  );
}
